import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { searchUser } from '../services/api';
import AsyncSelect from 'react-select/async';
import { FaTimes } from 'react-icons/fa';

// Simple debounce helper
function debounce(fn, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}

// Helper to get cache object from localStorage
function getCache(key) {
    const raw = localStorage.getItem(key);
    if (!raw) return { data: null, lastFetched: null };
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && 'data' in parsed && 'lastFetched' in parsed) {
            return parsed;
        } else {
            // Backward compatibility: treat as data only
            return { data: parsed, lastFetched: null };
        }
    } catch {
        return { data: null, lastFetched: null };
    }
}

// Search Player Modal Component
const SearchPlayerModal = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    // Get all available countries from locations cache
    const getAllCountries = () => {
        try {
            const locationsCache = getCache('locations').data || [];
            return locationsCache.map(location => location.name);
        } catch (error) {
            console.error('Error getting countries from locations:', error);
            return [];
        }
    };

    // Original API logic, unchanged
    const fetchPlayerOptions = async (inputValue, callback) => {
        console.log(`loadPlayerOptions called with: "${inputValue}" at ${new Date().toISOString()}`);
        if (!inputValue || inputValue.length < 2) {
            callback([]);
            return;
        }
        
        const countries = getAllCountries();
        if (countries.length === 0) {
            callback([]);
            return;
        }

        try {
            // Search across all countries
            const allResults = [];
            
            console.log(`🚀 Making API calls for "${inputValue}" across ${countries.length} countries`);
            
            // Search in parallel across all countries
            const searchPromises = countries.map(country => 
                searchUser(country, inputValue).catch(err => {
                    console.error(`Error searching in ${country}:`, err);
                    return [];
                })
            );
            
            const results = await Promise.all(searchPromises);
            const sessionUser = (() => {
                try {
                    const raw = localStorage.getItem('userProfile');
                    const parsed = raw ? JSON.parse(raw) : null;
                    return (parsed && parsed.data) ? parsed.data : null;
                } catch { return null; }
            })();
            const loggedInUserId = sessionUser?.userId;
            
            console.log(`✅ API calls completed for "${inputValue}" - found ${results.flat().length} total results`);
            
            // Combine and deduplicate results
            const seenUserIds = new Set();
            results.forEach(countryResults => {
                countryResults.forEach(user => {
                    if (!seenUserIds.has(user.userId) && user.userId !== loggedInUserId) {
                        seenUserIds.add(user.userId);
                        allResults.push(user);
                    }
                });
            });
            
            // Sort by name and limit to 20 results
            const sortedResults = allResults
                .sort((a, b) => a.name.localeCompare(b.name))
                .slice(0, 20);
            
            callback(sortedResults.map(user => ({
                value: user.userId,
                label: (
                    <div className="flex items-center">
                        {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full mr-2 object-cover" />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        )}
                        <div className="flex flex-col">
                            <span className="font-medium">{user.name}</span>
                            <span className="text-sm text-gray-500">
                                {user.location?.locality && `${user.location.locality}, `}
                                {user.location?.administrative_area_level_1 && `${user.location.administrative_area_level_1}, `}
                                {user.location?.country || 'Unknown'}
                            </span>
                        </div>
                    </div>
                ),
                user
            })));
        } catch (err) {
            console.error('Error searching players:', err);
            callback([]);
        }
    };

    // Debounced wrapper
    const loadPlayerOptions = useCallback(
        debounce((inputValue, callback) => {
            fetchPlayerOptions(inputValue, callback);
        }, 500), // allow up to half a second (500ms) between key strokes before calling API with inputValue
        []
    );

    // Handle player selection
    const handlePlayerSelect = (selected) => {
        if (selected) {
            setSelectedPlayer(selected.user);
            // Store player data temporarily for the profile page
            localStorage.setItem('tempPlayerProfile', JSON.stringify(selected.user));
            // Navigate to player profile page
            onClose();
            navigate(`/player/${selected.user.userId}`);
        } else {
            setSelectedPlayer(null);
        }
    };

    // Reset selected player when modal closes
    const handleClose = () => {
        setSelectedPlayer(null);
        onClose();
    };

    // Clear selected player when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedPlayer(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white text-black rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Search Player</h2>
                    <button 
                        onClick={handleClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FaTimes />
                    </button>
                </div>
                
                <div className="mb-4">
                    <AsyncSelect
                        cacheOptions
                        loadOptions={loadPlayerOptions}
                        defaultOptions={[]}
                        value={selectedPlayer ? {
                            value: selectedPlayer.userId,
                            label: (
                                <div className="flex items-center">
                                    {selectedPlayer.picture ? (
                                        <img src={selectedPlayer.picture} alt="" className="w-8 h-8 rounded-full mr-2 object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-gray-200 mr-2 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                    )}  
                                    <div className="flex flex-col">
                                        <span className="font-medium">{selectedPlayer.name}</span>
                                        <span className="text-sm text-gray-500">
                                            {selectedPlayer.location?.locality && `${selectedPlayer.location.locality}, `}
                                            {selectedPlayer.location?.administrative_area_level_1 && `${selectedPlayer.location.administrative_area_level_1}, `}
                                            {selectedPlayer.location?.country || 'Unknown'}
                                        </span>
                                    </div>
                                </div>
                            ),
                            user: selectedPlayer
                        } : null}
                        onChange={handlePlayerSelect}
                        placeholder="Type at least 2 characters to search..."
                        className="w-full"
                        classNamePrefix="select"
                        isClearable
                    />
                </div>

                <div className="flex gap-4 pt-4">
                    <button
                        onClick={handleClose}
                        className="flex-1 bg-gray-200 text-gray-700 py-2 rounded hover:bg-gray-300"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
    const [showSearchPlayer, setShowSearchPlayer] = useState(false);
    // New state for modals
    const [showSubmitMatch, setShowSubmitMatch] = useState(false);
    const [showJoinPool, setShowJoinPool] = useState(false);

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/login';
    };

    const navigate = useNavigate();

    return (
        <>
            <div className={`sidebar ${isOpen ? 'open' : ''}`}>
                <button onClick={toggleSidebar} className="close-button">X</button>
                <div className="p-4">
                    <ul className="mt-4">
                        {/* Dashboard */}
                        <li>
                            <Link to="/dashboard" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Dashboard
                            </Link>
                        </li>
                        {/* Search Player */}
                        <li>
                            <button 
                                onClick={() => { 
                                    setShowSearchPlayer(true); 
                                    toggleSidebar(); 
                                }} 
                                className="block w-full text-left py-2 hover:bg-gray-700"
                            >
                                Search Player
                            </button>
                        </li>
                        {/* Rankings */}
                        <li>
                            <Link to="/rankings" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Rankings
                            </Link>
                        </li>
                        {/* Submit Match */}
                        <li>
                            <button
                                onClick={() => {
                                    const t = Date.now();
                                    navigate(`/dashboard?showSubmitMatch=${t}`);
                                    toggleSidebar();
                                }}
                                className="block w-full text-left py-2 hover:bg-gray-700"
                            >
                                Submit Match
                            </button>
                        </li>
                        {/* My Matches */}
                        <li>
                            <Link to="/my-matches" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                My Matches
                            </Link>
                        </li>
                        {/* All Matches */}
                        <li>
                            <Link to="/all-matches" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                All Matches
                            </Link>
                        </li>
                        {/* View Pools */}
                        <li>
                            <Link to="/view-pools" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                View Pools
                            </Link>
                        </li>
                        {/* Join Pool */}
                        <li>
                            <button
                                onClick={() => {
                                    const t = Date.now();
                                    navigate(`/dashboard?showJoinPool=${t}`);
                                    toggleSidebar();
                                }}
                                className="block w-full text-left py-2 hover:bg-gray-700"
                            >
                                Join Pool
                            </button>
                        </li>
                        {/* Create Pool (was Add Pool) */}
                        <li>
                            <Link to="/pools/new" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Create Pool
                            </Link>
                        </li>
                        {/* Results Summary */}
                        <li>
                            <Link to="/results" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Results Summary
                            </Link>
                        </li>
                        {/* How Rankings Work */}
                        <li>
                            <Link to="/how-rankings-work" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                How Rankings Work
                            </Link>
                        </li>
                        {/* Terms & Conditions */}
                        <li>
                            <Link to="/terms-and-conditions" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Terms & Conditions
                            </Link>
                        </li>
                        {/* Contact Us */}
                        <li>
                            <Link to="/contact-us" className="block py-2 hover:bg-gray-700" onClick={toggleSidebar}>
                                Contact Us
                            </Link>
                        </li>
                        {/* Logout */}
                        <li>
                            <Link to="#" onClick={() => { handleLogout(); toggleSidebar(); }} className="block py-2 hover:bg-gray-700">
                                Logout
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Search Player Modal */}
            <SearchPlayerModal 
                isOpen={showSearchPlayer}
                onClose={() => setShowSearchPlayer(false)}
            />
        </>
    );
};

export default Sidebar;
